import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingBag, Eye, X, RefreshCw, User, Clock,
  ChevronLeft, ChevronRight, MapPin,
} from 'lucide-react';
import { api } from '../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WhatsAppLeadItem {
  productId: string;
  name: string;
  size: string;
  quantity: number;
  price: number;
}

interface WhatsAppLead {
  id: string;
  customerName: string;
  items: string; // JSON string
  totalAmount: number;
  itemCount: number;
  deliveryType: 'envio' | 'retiro';
  createdAt: number;
}

const LIMIT = 20;

function fmtPrice(n: number) {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
}

function fmtDate(unix: number): string {
  return new Date(unix * 1000).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export const Orders: React.FC = () => {
  const [items, setItems] = useState<WhatsAppLead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState<WhatsAppLead | null>(null);

  const fetchLeads = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const res = await api.get<{ items: WhatsAppLead[]; total: number }>(
        `/api/v1/analytics/leads?page=${page}&limit=${LIMIT}`
      );
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads(currentPage);
  }, [currentPage, fetchLeads]);

  const totalPages = Math.ceil(total / LIMIT);

  // Parse items from JSON
  const getParsedItems = (jsonStr: string): WhatsAppLeadItem[] => {
    try {
      return JSON.parse(jsonStr) as WhatsAppLeadItem[];
    } catch {
      return [];
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between md:items-center bg-page-surface px-5 py-3 rounded-2xl border border-page-border gap-4 shadow-sm">
        <div>
          <h2 className="font-heading font-bold text-lg text-ink leading-tight flex items-center gap-2">
            <span>Pedidos de WhatsApp</span>
            <span className="text-xs text-ink-faint font-semibold bg-white border border-page-border px-2 py-0.5 rounded-full">
              {total}
            </span>
          </h2>
          <p className="text-xs text-ink-muted mt-0.5">Historial de carritos enviados por clientes a WhatsApp.</p>
        </div>
        <button
          onClick={() => fetchLeads(currentPage)}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-page-border rounded-xl text-xs font-semibold text-ink-muted hover:text-ink hover:border-brand-border transition-colors self-start md:self-auto shadow-sm"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* Grid / Table */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <LoaderSpinner />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-ink-faint bg-page-surface rounded-2xl border border-page-border">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-ink-faint" />
          <p className="text-sm">No se registraron pedidos aún.</p>
        </div>
      ) : (
        <>
          {/* Mobile view */}
          <div className="md:hidden space-y-3">
            {items.map((lead) => (
              <div key={lead.id} className="bg-white border border-page-border rounded-2xl p-4 space-y-3 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-ink text-sm flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-ink-faint" />
                      {lead.customerName}
                    </h4>
                    <p className="text-[10px] text-ink-faint mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {fmtDate(lead.createdAt)}
                    </p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                    lead.deliveryType === 'envio'
                      ? 'bg-blue-50 border-blue-200 text-blue-600'
                      : 'bg-amber-50 border-amber-200 text-amber-600'
                  }`}>
                    {lead.deliveryType === 'envio' ? 'Envío' : 'Retiro'}
                  </span>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-page-border/50">
                  <div className="text-xs">
                    <span className="text-ink-muted">Pares:</span> <span className="font-bold text-ink">{lead.itemCount}</span>
                  </div>
                  <div className="text-sm font-bold text-ink">
                    {fmtPrice(lead.totalAmount)}
                  </div>
                </div>

                <button
                  onClick={() => setSelectedLead(lead)}
                  className="w-full py-2 bg-brand-surface border border-brand-border text-brand-primary rounded-xl transition-all flex items-center justify-center gap-1.5 text-xs font-bold"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Ver Detalle</span>
                </button>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto bg-white rounded-2xl border border-page-border shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-page-border text-ink-muted text-xs font-semibold uppercase tracking-wider bg-page-surface">
                  <th className="px-6 py-4">Cliente</th>
                  <th className="px-6 py-4">Fecha</th>
                  <th className="px-6 py-4">Método</th>
                  <th className="px-6 py-4 text-center">Pares</th>
                  <th className="px-6 py-4">Total</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-page-border text-sm">
                {items.map((lead) => (
                  <tr key={lead.id} className="hover:bg-page-surface transition-colors">
                    <td className="px-6 py-4 font-semibold text-ink">{lead.customerName}</td>
                    <td className="px-6 py-4 text-ink-muted text-xs">{fmtDate(lead.createdAt)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${
                        lead.deliveryType === 'envio'
                          ? 'bg-blue-50 border-blue-200 text-blue-600'
                          : 'bg-amber-50 border-amber-200 text-amber-600'
                      }`}>
                        {lead.deliveryType === 'envio' ? 'Envío' : 'Retiro'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-ink">{lead.itemCount}</td>
                    <td className="px-6 py-4 font-bold text-brand-primary">{fmtPrice(lead.totalAmount)}</td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="p-2 text-ink-faint hover:text-brand-primary hover:bg-brand-surface rounded-lg transition-colors border border-transparent hover:border-brand-border"
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-xs text-ink-faint">
                Pág. {currentPage} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                  className="p-1.5 border border-page-border hover:border-brand-border disabled:opacity-40 rounded-lg bg-white transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-ink-muted" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                  className="p-1.5 border border-page-border hover:border-brand-border disabled:opacity-40 rounded-lg bg-white transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-ink-muted" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 z-[9999] flex justify-center items-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setSelectedLead(null)} />

          {/* Modal Container */}
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-page-border animate-fade-scale z-10 overflow-hidden">
            {/* Header */}
            <div className="p-4 sm:p-5 border-b border-page-border flex justify-between items-center bg-page-surface">
              <div>
                <h3 className="font-heading font-bold text-lg text-ink">Detalle del Pedido</h3>
                <p className="text-[10px] text-ink-faint mt-0.5">ID: {selectedLead.id}</p>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-ink-faint hover:text-ink p-1.5 hover:bg-page-panel rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Cliente info */}
              <div className="grid grid-cols-2 gap-4 bg-page-surface p-4 rounded-xl border border-page-border/60">
                <div>
                  <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Cliente</span>
                  <span className="text-sm font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                    <User className="w-3.5 h-3.5 text-ink-faint" />
                    {selectedLead.customerName}
                  </span>
                </div>
                <div>
                  <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Método</span>
                  <span className="text-sm font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                    <MapPin className="w-3.5 h-3.5 text-ink-faint" />
                    {selectedLead.deliveryType === 'envio' ? 'Envío a domicilio' : 'Retiro por local'}
                  </span>
                </div>
                <div className="col-span-2 border-t border-page-border/40 pt-2.5">
                  <span className="block text-[9px] font-bold text-ink-faint uppercase tracking-wider">Fecha de Creación</span>
                  <span className="text-xs font-semibold text-ink flex items-center gap-1.5 mt-0.5">
                    <Clock className="w-3.5 h-3.5 text-ink-faint" />
                    {fmtDate(selectedLead.createdAt)}
                  </span>
                </div>
              </div>

              {/* Products list */}
              <div className="space-y-2.5">
                <span className="block text-[10px] font-bold text-ink-muted uppercase tracking-wider">Productos solicitados</span>
                <div className="divide-y divide-page-border border border-page-border rounded-xl overflow-hidden bg-white">
                  {getParsedItems(selectedLead.items).map((p, idx) => (
                    <div key={`${p.productId}-${idx}`} className="flex justify-between items-center p-3 hover:bg-page-surface transition-colors">
                      <div>
                        <h4 className="font-semibold text-ink text-xs">{p.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[9px] font-bold text-ink-muted bg-page-surface border border-page-border px-1 py-0.5 rounded">
                            Talle: {p.size}
                          </span>
                          <span className="text-[10px] text-ink-faint">
                            Cant: {p.quantity} x {fmtPrice(p.price)}
                          </span>
                        </div>
                      </div>
                      <div className="font-bold text-xs text-ink">
                        {fmtPrice(p.price * p.quantity)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-page-border pt-4 flex justify-between items-center">
                <span className="text-sm font-semibold text-ink-muted">Total del Pedido</span>
                <span className="text-xl font-bold text-brand-primary">{fmtPrice(selectedLead.totalAmount)}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-page-surface border-t border-page-border flex justify-end">
              <button
                onClick={() => setSelectedLead(null)}
                className="px-4 py-2 bg-white border border-page-border text-ink-muted hover:text-ink hover:border-brand-border rounded-xl text-xs font-bold shadow-sm transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LoaderSpinner: React.FC = () => (
  <div className="flex items-center gap-2 text-ink-muted text-xs">
    <RefreshCw className="w-4 h-4 animate-spin text-brand-primary" />
    <span>Cargando pedidos...</span>
  </div>
);
